(function () {
  if (customElements.get('product-card-slider')) return;

  class ProductCardSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      // When the browser is still parsing this element's own markup, connectedCallback
      // can fire BEFORE its children (track, buttons) exist yet. Defer one frame so
      // parsing finishes and querySelector actually finds them.
      requestAnimationFrame(() => this.init());
    }

    init() {
      this.track = this.querySelector('.product-card-slider__track');
      this.slides = Array.from(this.querySelectorAll('.product-card-slider__slide'));
      this.dots = Array.from(this.querySelectorAll('.product-card-slider__dot'));
      this.prevBtn = this.querySelector('.product-card-slider__btn--prev');
      this.nextBtn = this.querySelector('.product-card-slider__btn--next');
      this.index = 0;

      if (!this.track || !this.slides.length) return;
      this.initialized = true;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.go(-1);
          this.restartAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.go(1);
          this.restartAutoplay();
        });
      }

      // Autoplay: auto-advance every 3.5s, loops infinitely, pauses on hover/touch.
      this.autoplayDelay = 3500;
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.startAutoplay();

      this.initDrag();
    }

    initDrag() {
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragCurrentX = 0;
      this.dragThreshold = 40;
      this.wasDragged = false;

      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
      this.addEventListener('pointermove', (event) => this.handlePointerMove(event));
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event));
      this.addEventListener('pointercancel', (event) => this.handlePointerUp(event));
      this.addEventListener('pointerleave', (event) => {
        if (this.isDragging) this.handlePointerUp(event);
      });

      // If the user was dragging, swallow the click so the underlying
      // card/title link overlay doesn't navigate away by accident.
      this.addEventListener(
        'click',
        (event) => {
          if (this.wasDragged) {
            event.preventDefault();
            event.stopPropagation();
            this.wasDragged = false;
          }
        },
        true
      );
    }

    handlePointerDown(event) {
      if (event.target.closest('.product-card-slider__btn')) return;
      if (this.slides.length <= 1) return;
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragCurrentX = event.clientX;
      this.track.style.transition = 'none';
      this.stopAutoplay();
      if (this.setPointerCapture && event.pointerId !== undefined) {
        try {
          this.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }
    }

    handlePointerMove(event) {
      if (!this.isDragging) return;
      this.dragCurrentX = event.clientX;
      const deltaX = this.dragCurrentX - this.dragStartX;
      if (Math.abs(deltaX) > 5) {
        this.wasDragged = true;
        event.preventDefault();
      }
      const containerWidth = this.getBoundingClientRect().width || 1;
      const percentDelta = (deltaX / containerWidth) * 100;
      const basePercent = -(this.index * 100);
      this.track.style.transform = 'translateX(' + (basePercent + percentDelta) + '%)';
    }

    handlePointerUp(event) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.style.transition = '';
      const deltaX = this.dragCurrentX - this.dragStartX;
      if (this.releasePointerCapture && event && event.pointerId !== undefined) {
        try {
          this.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }
      if (Math.abs(deltaX) > this.dragThreshold) {
        if (deltaX < 0) {
          this.go(1);
        } else {
          this.go(-1);
        }
      } else {
        this.update();
      }
      this.startAutoplay();
      setTimeout(() => {
        this.wasDragged = false;
      }, 50);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.slides.length <= 1) return;
      // Random stagger so multiple sliders on the same page don't all move together.
      const staggerDelay = Math.random() * this.autoplayDelay;
      this.autoplayStartTimeout = setTimeout(() => {
        this.autoplayTimer = setInterval(() => this.go(1), this.autoplayDelay);
      }, staggerDelay);
    }

    stopAutoplay() {
      if (this.autoplayStartTimeout) {
        clearTimeout(this.autoplayStartTimeout);
        this.autoplayStartTimeout = null;
      }
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    go(direction) {
      const total = this.slides.length;
      if (!total) return;
      this.index = (this.index + direction + total) % total;
      this.update();
    }

    update() {
      if (this.track) {
        this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';
      }
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    }
  }

  customElements.define('product-card-slider', ProductCardSlider);
})();